import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link2, Plus, Building2, Clock, CheckCircle2, XCircle } from "lucide-react";

export default function ConsultantAccess() {
  const [requests, setRequests] = useState([]);
  const [linked, setLinked] = useState([]);
  const [open, setOpen] = useState(false);
  const [vat, setVat] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [r, l] = await Promise.all([api.get("/consultant/access-requests"), api.get("/consultant/linked-tenants")]);
      setRequests(r.data); setLinked(l.data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore caricamento"); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!vat.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/consultant/access-requests", { vat: vat.trim().toUpperCase() });
      if (data.status === "request_sent") toast.success(`Richiesta inviata a "${data.company_name}"`);
      else if (data.status === "already_pending") toast.info(`Richiesta già inviata a "${data.company_name}"`);
      else if (data.status === "already_approved") toast.success(`Già collegato a "${data.company_name}"`);
      else if (data.status === "company_created_under_consultant") toast.success(`Azienda con P.IVA ${data.vat} aggiunta al tuo portfolio (nessuna licenza esistente)`);
      setOpen(false); setVat(""); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore invio richiesta"); }
    finally { setSaving(false); }
  };

  const pending = requests.filter((r) => r.status === "pending");
  const approved = linked;
  const rejected = requests.filter((r) => r.status === "rejected");

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-indigo-600 mb-2">Portafoglio Aziende</div>
          <h1 className="font-display font-black text-4xl md:text-5xl">Aggancia P.IVA</h1>
          <p className="text-slate-500 mt-2 text-sm max-w-xl">Inserisci la P.IVA di un'azienda per collegarla. Se ha già un portale attivo, verrà inviata richiesta di approvazione. Altrimenti sarà creata nel tuo portfolio.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-link-btn" className="h-11 bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4" /> Aggancia P.IVA</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display font-black text-2xl">Aggancia una nuova azienda</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-600">P.IVA dell'azienda *</Label>
                <Input data-testid="link-vat-input" required value={vat} onChange={(e) => setVat(e.target.value.toUpperCase())} className="mt-2 h-11 font-mono-plex" placeholder="IT01234567890" />
                <div className="mt-2 text-[11px] text-slate-500 leading-relaxed">
                  • Se l'azienda ha già un portale Audit Pro, verrà inviata una richiesta di collegamento (deve approvare).<br/>
                  • Se non esiste, l'azienda verrà creata nel tuo portfolio come cliente non-licenziato (puoi comunque farci audit).
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="link-submit-btn" className="bg-indigo-600 hover:bg-indigo-700"><Link2 className="h-4 w-4" /> {saving ? "Invio…" : "Aggancia"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {pending.length > 0 && (
        <div className="mb-8">
          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" /> In attesa di approvazione ({pending.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {pending.map((r) => (
              <div key={r.id} data-testid={`pending-${r.id}`} className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-4 w-4 text-amber-700" />
                  <span className="font-display font-bold text-slate-900">{r.company_name}</span>
                </div>
                <div className="text-xs text-slate-600 font-mono-plex">{r.company_vat}</div>
                <div className="text-[11px] text-slate-500 mt-1">Richiesta inviata il {r.created_at?.slice(0, 10)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-8">
        <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> Aziende collegate ({approved.length})</h2>
        {approved.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-slate-500 text-sm">
            Nessun collegamento approvato. Aggancia una P.IVA per iniziare.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {approved.map((r) => (
              <div key={r.id} data-testid={`linked-${r.id}`} className="bg-white border border-emerald-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-4 w-4 text-emerald-700" />
                  <span className="font-display font-bold text-slate-900">{r.company_name}</span>
                </div>
                <div className="text-xs text-slate-600 font-mono-plex">{r.company_vat}</div>
                <div className="text-[11px] text-slate-500 mt-1">Approvata il {r.approved_at?.slice(0, 10)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {rejected.length > 0 && (
        <div>
          <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><XCircle className="h-5 w-5 text-red-600" /> Richieste rifiutate ({rejected.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {rejected.map((r) => (
              <div key={r.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="font-display font-bold text-slate-900">{r.company_name}</div>
                <div className="text-xs text-slate-600 font-mono-plex mt-1">{r.company_vat}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
